import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import type {
  PaymentIntent as StripePaymentIntent,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js";
import PaymentReceipt from "./PaymentReceipt";
import { useDispatch } from "react-redux";
import { apiSlice } from "components/Slices/apiSlice";
import { useUpdatePaymentMethodMutation, useConfirmDepositMutation } from "components/Slices/usersApiSlice";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  width: "60%",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflow: "auto",
};

interface FeeBreakdown {
  baseAmount: number;
  stripeFee: number;
  platformFee: number;
  totalFees: number;
  totalAmount: number;
  paymentMethodType: string;
}

interface DepositData {
  clientSecret: string;
  customerSession: string;
  paymentIntentId: string;
  baseAmount: number;
  currency: string;
  feesByMethod: Record<string, FeeBreakdown>;
  selectedMethod: string;
}

interface DepositModalProps {
  open: boolean;
  handleClose: () => void;
  circleId: string;
  depositData: DepositData;
}

// Map Stripe payment method types to display names
const methodDisplayNames: Record<string, string> = {
  card: "Credit/Debit Card",
  acss_debit: "Bank Account (Pre-authorized Debit)",
  us_bank_account: "Bank Account (ACH)",
};

const DepositModal: React.FC<DepositModalProps> = ({
  open,
  handleClose,
  circleId,
  depositData,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const [confirmDeposit] = useConfirmDepositMutation();

  const [paymentIntent, setPaymentIntent] = useState<
    StripePaymentIntent | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "idle" | "processing" | "confirming" | "success" | "error"
  >("idle");

  const [selectedMethod, setSelectedMethod] = useState<string>(
    depositData.selectedMethod
  );
  const [currentFees, setCurrentFees] = useState<FeeBreakdown>(
    depositData.feesByMethod[depositData.selectedMethod]
  );
  const [isUpdatingFees, setIsUpdatingFees] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);

  // Handle payment method change from PaymentElement
  const handlePaymentElementChange = async (
    event: StripePaymentElementChangeEvent
  ) => {
    // Track whether all required fields are complete
    setIsPaymentComplete(event.complete);

    if (!event.value?.type) return;

    const newMethodType = event.value.type;

    // Only update if method type changed
    if (newMethodType !== selectedMethod && depositData.feesByMethod[newMethodType]) {
      setSelectedMethod(newMethodType);
      setCurrentFees(depositData.feesByMethod[newMethodType]);
      setIsUpdatingFees(true);

      try {
        // Update PaymentIntent with new amount
        await updatePaymentMethod({
          paymentIntentId: depositData.paymentIntentId,
          paymentMethodType: newMethodType,
        }).unwrap();
      } catch (err) {
        console.error("Error updating payment method:", err);
      } finally {
        setIsUpdatingFees(false);
      }
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    if (status === "processing" || isUpdatingFees) return;
    event.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage("Cannot connect to payment gateway.");
      return;
    }
    setStatus("processing");

    const res = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/circles/${circleId}/success`,
      },
      redirect: "if_required",
    });
    setPaymentIntent(res.paymentIntent);

    if (res.error) {
      setErrorMessage(res.error.message || "Something went wrong.");
      setStatus("error");
    } else if (res.paymentIntent) {
      // Show confirming state while we update the backend
      setStatus("confirming");

      // Immediately confirm and process the deposit
      // This ensures the status is updated before any page refresh
      try {
        await confirmDeposit({
          paymentIntentId: res.paymentIntent.id,
          circleId,
        }).unwrap();
      } catch (err) {
        // Webhook will handle it if this fails, just log
        console.log("Confirm deposit fallback to webhook:", err);
      }

      // Invalidate cache to refetch updated status
      dispatch(
        apiSlice.util.invalidateTags([{ type: "Status", id: circleId }])
      );

      // Small delay to ensure cache is updated before showing success
      await new Promise((resolve) => setTimeout(resolve, 500));

      setStatus("success");
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)} ${depositData.currency}`;
  };

  // Prevent closing modal during payment processing
  const canClose = status === "idle" || status === "success" || status === "error";

  return (
    <Modal open={open} onClose={canClose ? handleClose : undefined}>
      <Box sx={style}>
        {status !== "success" && status !== "confirming" && (
          <form onSubmit={handleSubmit}>
            {/* Fee Breakdown */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: "grey.50",
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Contribution Amount</Typography>
                <Typography>{formatCurrency(currentFees.baseAmount)}</Typography>
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography color="text.secondary" variant="body2">
                  Processing Fee (Stripe)
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {formatCurrency(currentFees.stripeFee)}
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography color="text.secondary" variant="body2">
                  Platform Fee (1.5%)
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {formatCurrency(currentFees.platformFee)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography fontWeight="bold">Total</Typography>
                <Typography fontWeight="bold" color="primary">
                  {isUpdatingFees ? (
                    <CircularProgress size={16} />
                  ) : (
                    formatCurrency(currentFees.totalAmount)
                  )}
                </Typography>
              </Box>

              {/* Show savings for bank payments */}
              {selectedMethod !== "card" &&
                depositData.feesByMethod.card && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    You save{" "}
                    {formatCurrency(
                      depositData.feesByMethod.card.totalFees -
                        currentFees.totalFees
                    )}{" "}
                    by paying with {methodDisplayNames[selectedMethod] || "bank transfer"}!
                  </Alert>
                )}
            </Paper>

            {/* Payment Element */}
            <PaymentElement
              options={{ layout: "accordion" }}
              onChange={handlePaymentElementChange}
            />

            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!stripe || !isPaymentComplete || status === "processing" || isUpdatingFees}
              sx={{ mt: 2, width: "100%" }}
            >
              {status === "processing" ? (
                <CircularProgress color="info" size={24} />
              ) : (
                `Pay ${formatCurrency(currentFees.totalAmount)}`
              )}
            </Button>

            {!isPaymentComplete && status === "idle" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please complete all payment details above to continue.
              </Alert>
            )}
            {status === "error" && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}
            {status === "processing" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Processing payment...
              </Alert>
            )}
          </form>
        )}

        {status === "confirming" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Confirming your payment...
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              Please wait while we confirm your payment. Do not close this window.
            </Typography>
          </Box>
        )}

        {status === "success" && paymentIntent && (
          <PaymentReceipt
            paymentId={paymentIntent.id}
            amount={(paymentIntent.amount / 100).toFixed(2)}
            currency={paymentIntent.currency.toUpperCase()}
            status={paymentIntent.status}
            paymentMethod={paymentIntent.payment_method as string}
            date={new Date(paymentIntent.created * 1000).toLocaleString()}
          />
        )}
      </Box>
    </Modal>
  );
};

export default DepositModal;
